import React, { useState, useEffect, useCallback } from "react";
import { apiErrorText } from '../lib/apiError';
import axios from "axios";
import { toast } from "sonner";
import { Handshake, RefreshCw, Loader2, MailCheck, MailX, StickyNote, Ticket, Send } from "lucide-react";
import { BACKEND_URL } from "../config/api";

const STATUS_OPTIONS = ["applied", "active", "paused", "rejected"];
const STATUS_STYLES = {
  applied: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-slate-100 text-slate-600 border-slate-200",
  rejected: "bg-red-50 text-red-600 border-red-200",
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("jwtToken")}` });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const SuperAdminReferralsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [notesOpen, setNotesOpen] = useState(null); // affiliate id
  const [notesDraft, setNotesDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/v2/super-admin/referral-affiliates`, { headers: authHeaders() });
      setRows(res.data.affiliates || []);
    } catch (e) {
      if (e.response?.status !== 401) toast.error(apiErrorText(e, "Failed to load referral affiliates"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, okMsg) => {
    setSavingId(id);
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/v2/super-admin/referral-affiliates/${id}`, body, { headers: authHeaders() });
      const updated = res.data.affiliate;
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));
      toast.success(okMsg);
    } catch (e) {
      toast.error(apiErrorText(e, "Update failed"));
    } finally {
      setSavingId(null);
    }
  };

  const resendKit = async (id, name) => {
    setSavingId(id);
    try {
      await axios.post(`${BACKEND_URL}/api/v2/super-admin/referral-affiliates/${id}/resend-onboarding`, {}, { headers: authHeaders() });
      toast.success(`Onboarding kit re-sent to ${name}`);
    } catch (e) {
      toast.error(apiErrorText(e, "Resend failed"));
    } finally {
      setSavingId(null);
    }
  };

  const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: rows.filter((r) => r.status === s).length }), {});

  return (
    <div data-testid="superadmin-referrals-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Handshake className="w-5 h-5" strokeWidth={1.75} />
            </span>
            Referral Program
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            OTP-verified affiliates — independent of operator orgs. Payouts are manual: 10–40% of BookingFlows&apos; profit from each referred company, 12 months.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="referrals-refresh-button"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Stat chips */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="referrals-stat-chips">
        <div className="px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm">
          <span className="font-bold text-slate-900">{rows.length}</span> <span className="text-slate-500">total</span>
        </div>
        {STATUS_OPTIONS.map((s) => (
          <div key={s} className={`px-4 py-2 rounded-xl border text-sm ${STATUS_STYLES[s]}`}>
            <span className="font-bold">{counts[s] || 0}</span> {s}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-200 rounded-2xl" data-testid="referrals-empty-state">
          <Handshake className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No referral affiliates yet.</p>
          <p className="text-sm text-slate-400 mt-1">Applications from /referral-program land here after OTP verification.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Affiliate</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Referral notes</th>
                <th className="px-4 py-3">Tickets</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <React.Fragment key={r.id}>
                  <tr data-testid={`referral-affiliate-row-${i}`} className="align-top hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.role || "—"}{r.company ? ` · ${r.company}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        {r.emailVerified
                          ? <MailCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" title="Email verified (OTP)" />
                          : <MailX className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                        <span className="truncate max-w-[220px]">{r.email}</span>
                      </div>
                      {r.phone && <div className="text-xs text-slate-500 mt-0.5">{r.phone}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(r.createdAt)}</td>
                    <td className="px-4 py-3 max-w-[260px]">
                      <div className="text-xs text-slate-600 whitespace-pre-line line-clamp-3">{r.referralNotes || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {(r.tickets || []).length === 0 ? <span className="text-slate-400 text-xs">—</span> :
                          r.tickets.map((t) => (
                            <span key={t.ticketNumber}
                                  title={`${t.category} · ${t.status}`}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-mono ${t.status === "resolved" ? "bg-slate-50 text-slate-500 border-slate-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                              <Ticket className="w-3 h-3" /> {t.ticketNumber}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select value={r.status} disabled={savingId === r.id}
                              onChange={(e) => patch(r.id, { status: e.target.value }, `${r.name} → ${e.target.value}`)}
                              data-testid={`referral-status-select-${i}`}
                              className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold focus:outline-none ${STATUS_STYLES[r.status] || "border-slate-200"}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => { setNotesOpen(notesOpen === r.id ? null : r.id); setNotesDraft(r.internalNotes || ""); }}
                                data-testid={`referral-notes-toggle-${i}`}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${r.internalNotes ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "text-slate-500 hover:bg-slate-100"}`}>
                          <StickyNote className="w-3.5 h-3.5" /> Notes
                        </button>
                        <button onClick={() => resendKit(r.id, r.name)} disabled={savingId === r.id}
                                data-testid={`referral-resend-kit-${i}`}
                                title="Re-send the 3-email onboarding kit (guidelines, sell-sheet, brand)"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50">
                          <Send className="w-3.5 h-3.5" /> Kit
                        </button>
                      </div>
                    </td>
                  </tr>
                  {notesOpen === r.id && (
                    <tr className="bg-amber-50/40">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="max-w-2xl">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Internal notes (payouts, agreement, vetting)</label>
                          <textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={3}
                                    data-testid={`referral-notes-textarea-${i}`}
                                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none" />
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => { patch(r.id, { internal_notes: notesDraft }, "Notes saved"); setNotesOpen(null); }}
                                    disabled={savingId === r.id}
                                    data-testid={`referral-notes-save-${i}`}
                                    className="px-3.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50">
                              Save
                            </button>
                            <button onClick={() => setNotesOpen(null)}
                                    className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors">
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SuperAdminReferralsTab;
